import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Input,
    ListGroup,
    ListGroupItem,
    Container,
    Row,
    Col,
    Button,
    Pagination,
    PaginationItem,
    Table,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Card,
    CardBody,
    Label,
    FormGroup,
    Badge
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { productActions } from '../../sagas/productSlice';
import { debounce } from 'lodash';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaCalendarAlt, FaEnvelope, FaHistory, FaMapMarkerAlt, FaMinus, FaMoneyBillWave, FaPlus, FaPrint, FaQrcode, FaReceipt, FaSearch, FaShoppingCart, FaStore, FaTag, FaTrash, FaUndoAlt, FaUser, FaWhatsapp } from 'react-icons/fa'; // Import trash icon
import { refundActions } from '../../sagas/refundSlice';
import { useNavigate } from 'react-router-dom';
import emptyCart from "../../assets/images/empty-cart.png";
import { formatPrice } from '../../helpers/utility';

const RefundForm = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [refundItems, setRefundItems] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [criteria, setSearchCriteria] = useState({ query: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState([]);

    const {
        products,
        loading,
        totalPages,
        totalElements,
        pageNumber,
        pageSize,
        error,
    } = useSelector((state) => state.products);

    useEffect(() => {
        let payload = {
            criteria,
            page: currentPage,
            size: 10,
        };
        dispatch(productActions.search(payload));
    }, [currentPage]);

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [products]);

    const debouncedSearch = debounce((term) => {
        const searchLower = term.toLowerCase();
        dispatch(productActions.search({
            criteria: {
                query: term,
            },
            page: 1,
            size: 10,
            onSuccess: (items) => {
                setIsSearching(false)
                // Check for exact barcode match

                const matchedItems = items?.filter(p =>
                    p.barcode?.toLowerCase().includes(searchLower) ||
                    p.code?.toLowerCase().includes(searchLower) ||
                    p.description?.toLowerCase().includes(searchLower)
                );


                if (matchedItems.length === 1) {
                    // Add to cart directly
                    handleAddProduct(matchedItems[0]);
                    setSearchTerm('');
                } else if (matchedItems.length > 1 && term.length >= 3) {
                    // Show modal with search results
                    setIsModalOpen(true);
                }
            }
        }));
    }, 1000);


    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const handleAddProduct = (product) => {
        console.log('Adding product:', product);
        const existingItem = refundItems.find(item => item.productId === product.id);
        if (existingItem) {
            handleUpdateQuantity(existingItem.productId, existingItem.quantity + 1);
        } else {
            setRefundItems([...refundItems, {
                barcode: product.barcode,
                productId: product.id,
                productDescription: product.description,
                price: product.userPrice,
                quantity: 1,
                total: product.userPrice
            }]);
        }
    };

    const handleUpdateQuantity = (productId, newQuantity) => {
        if (newQuantity < 1) return;
        setRefundItems(refundItems.map(item =>
            item.productId === productId
                ? { ...item, quantity: newQuantity, total: item.price * newQuantity }
                : item
        ));
    };

    const handleUpdateUnitPrice = (productId, newUnitPrice) => {
        setRefundItems(refundItems.map(item =>
            item.productId === productId
                ? { ...item, price: newUnitPrice, total: newUnitPrice * item.quantity }
                : item
        ));
    };

    const handleRemoveItem = (productId) => {
        setRefundItems(refundItems.filter(item => item.productId !== productId));
    };

    const handleClearCart = () => {
        Swal.fire({
            title: t('cart.clearConfirmMessage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
        }).then((result) => {
            if (result.isConfirmed) {
                setRefundItems([]);
                setTotalAmount(0);
            }
        });
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            debouncedSearchForEnterKey(searchTerm);
        }
    };

    const debouncedSearchForEnterKey = debounce((term) => {
        const searchLower = term.toLowerCase();
        dispatch(productActions.search({
            criteria: {
                query: term,
            },
            page: 1,
            size: 500,
            onSuccess: (items) => {
                setIsSearching(false)
                // Check for exact barcode match

                const matchedItems = items?.filter(p =>
                    p.barcode?.toLowerCase().includes(searchLower) ||
                    p.code?.toLowerCase().includes(searchLower) ||
                    p.description?.toLowerCase().includes(searchLower)
                );


                if (matchedItems.length === 1) {
                    // Add to cart directly
                    handleAddProduct(matchedItems[0]);
                    setSearchTerm('');
                } else if (matchedItems.length > 1 && term.length >= 3) {
                    // Show modal with search results
                    setIsModalOpen(true);
                }
            }
        }));
    }, 1000);


    const handleSubmit = async () => {
        try {
            dispatch(refundActions.addRefund(
                {
                    data: { items: refundItems, total: totalAmount },
                    onSuccess: (result) => {
                        setRefundItems([]);
                        setTotalAmount(0);
                        Swal.fire({
                            title: t('refund.successTitle'),
                            text: t('refund.successMessage'),
                            icon: 'success'
                        });
                        // Generate and print bill
                        generatePDF(result);
                    }
                }
            ));

        } catch (error) {
            Swal.fire({
                title: t('refund.errorTitle'),
                text: error.message,
                icon: 'error'
            });
        }
    };

    const generatePDF = async (receiptData) => {
        try {
            let payload = {
                id: receiptData.id,
                onSuccess: (response) => {
                    const pdfWindow = window.open();
                    pdfWindow.document.write(
                        "<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
                        encodeURI(response) +
                        "'></iframe>"
                    );
                },
            }

            dispatch(refundActions.printReceipt(payload))

        } catch (error) {
            console.error('PDF generation error:', error);
        }
    };

    const handleCheckboxChange = (productId, checked) => {
        if (checked) {
            setSelectedProductIds((prev) => [...prev, productId]);
        } else {
            setSelectedProductIds((prev) => prev.filter((id) => id !== productId));
        }
    };

    const handleAddSelectedProducts = () => {
        // Find the selected products from searchResults
        const chosen = products.filter((p) => selectedProductIds.includes(p.id));
        // Add all selected products using functional state update to avoid stale state
        setRefundItems((prevItems) => {
            let updatedItems = [...prevItems];
            chosen.forEach(product => {
                const existingItem = updatedItems.find(item => item.productId === product.id);
                if (existingItem) {
                    // If already exists, increment quantity
                    existingItem.quantity += 1;
                    existingItem.total = existingItem.price * existingItem.quantity;
                } else {
                    updatedItems.push({
                        barcode: product.barcode,
                        productId: product.id,
                        productDescription: product.description,
                        price: product.userPrice,
                        quantity: 1,
                        total: product.userPrice
                    });
                }
            });
            return updatedItems;
        });
        setIsModalOpen(false);
        setSearchTerm('');
        setSelectedProductIds([]);
    };

    useEffect(() => {
        const total = refundItems.reduce((sum, item) => sum + item.total, 0);
        setTotalAmount(total);
    }, [refundItems]);

    return (
        <Container fluid>
            <Row>
                <Col md={12} className="ps-4 border-start">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="position-relative flex-grow-1 me-3">
                            <Input
                                innerRef={searchInputRef}
                                type="text"
                                placeholder={t('product.searchFocusHint')}
                                value={searchTerm}
                                onKeyDown={handleSearchKeyDown}
                                onChange={handleSearchChange}
                                className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                                title={t('product.searchShortcut')}
                            />
                            <FaSearch
                                style={{
                                    position: 'absolute',
                                    left: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#adb5bd'
                                }}
                                size={14}
                            />
                            <FaBarcode
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#adb5bd'
                                }}
                                size={20}
                                title={t('product.scanHint')}
                            />
                        </div>
                        <Button
                            color="primary"
                            className="d-flex align-items-center"
                            onClick={() => navigate('/refund/history')}
                            title={t('refund.viewHistory')}
                        >
                            <FaHistory className="me-2" />
                            {t('refund.viewHistory')}
                        </Button>
                    </div>
                    <Card>
                        <div className="card-header bg-light d-flex justify-content-between align-items-center py-3">
                            <div className="d-flex align-items-center">
                                <div className="position-relative me-2">
                                    <FaShoppingCart size={18} className="text-primary" />
                                    {refundItems.length > 0 && (
                                        <Badge
                                            color="danger"
                                            pill
                                            className="position-absolute d-flex align-items-center justify-content-center"
                                            style={{
                                                top: '-6px',
                                                right: '-10px',
                                                fontSize: '0.65rem',
                                                width: '16px',
                                                height: '16px',
                                                padding: 0
                                            }}
                                        >
                                            {refundItems.reduce((total, item) => total + item.quantity, 0)}
                                        </Badge>
                                    )}
                                </div>
                                <h6 className="mb-0 fw-bold">{t('refund.items')}</h6>
                            </div>

                            {refundItems.length > 0 && (
                                <Button
                                    color="danger"
                                    size="sm"
                                    onClick={handleClearCart}
                                >
                                    <FaTrash size={12} />
                                </Button>
                            )}
                        </div>
                        <CardBody className="p-0">
                            {refundItems.length === 0 ? (
                                <div className="text-center py-5">
                                    <img
                                        src={emptyCart}
                                        alt="Empty Cart"
                                        style={{
                                            width: '150px',
                                            opacity: '0.5',
                                            marginBottom: '1rem'
                                        }}
                                    />
                                    <p className="text-muted">{t('refund.empty')}</p>
                                </div>
                            ) : (
                                <>
                                    <Card className="mb-3">
                                        <CardBody className="p-0">
                                            <div style={{
                                                height: '400px',
                                                overflowY: 'auto'
                                            }}>
                                                <Table hover responsive className="table-cart">
                                                    <thead>
                                                        <tr>
                                                            <th>{t('product.barcode')}</th>
                                                            <th>{t('product.description')}</th>
                                                            <th>{t('refund.quantity')}</th>
                                                            <th>{t('refund.unitPrice')}</th>
                                                            <th>{t('refund.total')}</th>
                                                            <th>{t('common.actions')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {refundItems.map(item => (
                                                            <tr key={item.productId}>
                                                                <td>{item.barcode}</td>
                                                                <td>{item.productDescription}</td>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <Button size="sm" onClick={() => handleUpdateQuantity(item.productId, item.quantity - 1)}>
                                                                            <FaMinus />
                                                                        </Button>
                                                                        <Input
                                                                            type="number"
                                                                            value={item.quantity}
                                                                            onChange={(e) => handleUpdateQuantity(item.productId, parseInt(e.target.value))}
                                                                            className="mx-2"
                                                                            onFocus={(e) => e.target.select()} // Select the field content on focus
                                                                            style={{ width: '120px' }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    e.preventDefault();
                                                                                    searchInputRef.current?.focus();
                                                                                }
                                                                            }}
                                                                        />
                                                                        <Button size="sm" onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1)}>
                                                                            <FaPlus />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="number"
                                                                        step="0.1"
                                                                        className="form-control form-control-sm text-center"
                                                                        value={item.price}
                                                                        onFocus={(e) => e.target.select()} // Select the field content on focus
                                                                        onChange={(e) => handleUpdateUnitPrice(item.productId, e.target.value)}
                                                                        style={{ width: '120px' }}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') {
                                                                                e.preventDefault();
                                                                                searchInputRef.current?.focus();
                                                                            }
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td>{formatPrice(item.total)}</td>
                                                                <td>
                                                                    <Button color="danger" size="sm" onClick={() => handleRemoveItem(item.productId)}>
                                                                        <FaTrash />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                    <div className="border-top p-3">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="fs-5 fw-bold text-muted ps-2">
                                                {t('refund.totalToRefund')}
                                            </div>
                                            <div className="fs-4 fw-bold text-primary pe-2">
                                                {formatPrice(totalAmount)}
                                            </div>
                                        </div>

                                        <Button
                                            color="success"
                                            className="w-100 mt-1 py-2"
                                            size="lg"
                                            onClick={handleSubmit}
                                        >
                                            <FaShoppingCart className="me-2" />
                                            {t('refund.confirm')}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {isModalOpen && (
                <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)} size='lg'>
                    <ModalHeader toggle={() => setIsModalOpen(false)}>
                        {t('common.searchResults')}
                    </ModalHeader>
                    <ModalBody >
                        <div className="cart-container" style={{
                            height: '500px',
                            overflowY: 'auto'
                        }}>
                            <Table bordered hover>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{t('inventory.codeArticle')}</th>
                                        <th style={{ width: '50%' }}>{t('product.description')}</th>
                                        <th>{t('product.buyingPrice')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((product) => (
                                        <tr key={product.id} style={{ whiteSpace: 'nowrap', height: '20px' }}>
                                            <td>
                                                <Input
                                                    type="checkbox"
                                                    checked={selectedProductIds.includes(product.id)}
                                                    onChange={(e) => handleCheckboxChange(product.id, e.target.checked)}
                                                />
                                            </td>
                                            <td>{product.barcode}</td>
                                            <td>{product.description}</td>
                                            <td>{formatPrice(product.buyingPrice)}</td>
                                        </tr>
                                    ))}
                                </tbody>

                            </Table>
                            <div className="d-flex align-items-center gap-2">
                                <Button color="light" size="sm" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
                                    <FaAngleDoubleLeft />
                                </Button>
                                <Button color="light" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                    <FaAngleLeft />
                                </Button>

                                <span className="mx-2">
                                    {currentPage} / {totalPages}
                                </span>

                                <Button color="light" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                                    <FaAngleRight />
                                </Button>
                                <Button color="light" size="sm" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
                                    <FaAngleDoubleRight />
                                </Button>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={handleAddSelectedProducts} disabled={selectedProductIds.length === 0}>
                            {t('common.addSelected')}
                        </Button>
                    </ModalFooter>
                </Modal>
            )}
        </Container>
    );
};

export default RefundForm;